import { Box, useTheme, IconButton, Divider, Typography, useMediaQuery } from '@mui/material'
import { Dispatch, SetStateAction, useRef, useEffect } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'

import { useTranslation } from 'react-i18next'
import { Close as CloseIcon } from '@mui/icons-material'

import { BlockchainModalItem } from '../blockchain-modal-item'
import { MediaSection } from '../scan-media-section'
import { CustomModalAnimated } from 'src/components/modal-animated'
import { BlockchainVerifiedModalHeader } from './header'
import { BlockchainMapItem } from '../blockchain-map-item'

import { formatDate, getPolygonscanAddressUrl, getScanExplorerTxUrl } from 'src/utils'
import { IScan } from 'src/types'
import { ExternalLinkIcon } from 'src/assets/icons/v2/ExternalLinkIcon'
import { COLORS } from 'src/styles/common'

import 'swiper/css'
import { AvatarImg } from 'src/components/step-info/step-company.styled'

interface IProps {
  open: boolean
  handleClose: () => void
  handlePrev: (scanLength: number) => void
  handleNext: (scanLength: number) => void
  currentIndex: number
  scans: IScan[]
  setCurrentIndex: Dispatch<SetStateAction<number>>
  primaryColor?: string
  walletAddress: string
  showWalletAddress: boolean
  accountImage?: string
  scanImage?: string
  scanName?: string
  impactImage?: string
  impactName?: string
}

// const maskEmail = (email: string) => {
//   const [localPart, domain] = email.split('@')
//   const maskedLocalPart = '&#8727;'.repeat(localPart.length)
//   return (
//     <span>
//       <span dangerouslySetInnerHTML={{ __html: maskedLocalPart }} />
//       {`@${domain}`}
//     </span>
//   )
// }

const checkGeofenceStatus = (locations: { within_geofence?: boolean }[]): boolean => {
  return locations && locations.some((location) => location?.within_geofence)
}

export const BlockchainVerifiedModal = ({
  open,
  handleClose,
  handlePrev,
  handleNext,
  currentIndex,
  scans = [],
  setCurrentIndex,
  primaryColor,
  walletAddress,
  showWalletAddress,
  accountImage,
  scanImage,
  scanName,
  impactImage,
  impactName,
}: IProps) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const swiperRef = useRef<SwiperType | null>(null)
  const isInitializingRef = useRef<boolean>(false)
  const targetIndexRef = useRef<number>(currentIndex)
  const hasSetInitialSlideRef = useRef<boolean>(false)
  const isLoopMode = scans.length > 1

  // Sync targetIndexRef when modal opens
  useEffect(() => {
    if (open) {
      // When modal opens, ensure targetIndexRef matches currentIndex
      targetIndexRef.current = currentIndex
    } else {
      // Reset flag when modal closes
      hasSetInitialSlideRef.current = false
    }
  }, [open, currentIndex])

  const handleSlideChange = (swiper: any) => {
    // Don't update state during initialization
    if (swiper && !isInitializingRef.current) {
      const realIndex = swiper.realIndex

      // Ensure index is valid and not undefined/null
      if (typeof realIndex === 'number' && realIndex >= 0 && realIndex < scans.length) {
        setCurrentIndex(realIndex)
      }
    }
  }

  const truncateAddress = (addr: string) => `${addr.slice(0, 12)}...${addr.slice(-4)}`

  const headerHeight = () => {
    let height = 185

    if (!showWalletAddress) {
      height -= 25
    }

    if (scans.length === 1 && showWalletAddress) {
      height -= 50
    } else if (scans.length === 1 && !showWalletAddress) {
      height -= 20
    }

    return height
  }

  return (
    <CustomModalAnimated ariaLable="modal-scan" open={open} handleClose={handleClose}>
      <Box
        id="index__components-blockchain-verified-banner-blockchain-modal__box__l101"
        sx={{
          zIndex: 101,
        }}
      >
        <Swiper
          key={open ? 'swiper-open' : 'swiper-closed'}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
          onInit={(swiper) => {
            if (open && !hasSetInitialSlideRef.current) {
              isInitializingRef.current = true
              hasSetInitialSlideRef.current = true
              const targetIndex = targetIndexRef.current
              // Ensure targetIndex is valid
              const validIndex = Math.max(0, Math.min(targetIndex, scans.length - 1))

              // Set initial index immediately to prevent NaN
              if (typeof validIndex === 'number' && validIndex >= 0) {
                setCurrentIndex(validIndex)
              }

              // Wait for loop mode to initialize, then set slide
              // Use slideToLoop when loop mode is enabled, otherwise use slideTo
              setTimeout(() => {
                try {
                  if (isLoopMode && scans.length > 1) {
                    // For loop mode, use slideToLoop which handles the loop index correctly
                    swiper.slideToLoop(validIndex, 0)
                  } else {
                    swiper.slideTo(validIndex, 0)
                  }

                  // Verify the slide was set correctly
                  setTimeout(() => {
                    const realIndex = swiper.realIndex
                    // Ensure we have a valid index before setting
                    if (
                      typeof realIndex === 'number' &&
                      realIndex >= 0 &&
                      realIndex < scans.length
                    ) {
                      // Update currentIndex to match the actual slide position
                      setCurrentIndex(realIndex)
                    } else {
                      // Fallback to target index if realIndex is invalid
                      setCurrentIndex(validIndex)
                    }
                    isInitializingRef.current = false
                  }, 100)
                } catch (error) {
                  // If there's an error, just set the index and continue
                  console.warn('Error setting initial slide:', error)
                  setCurrentIndex(validIndex)
                  isInitializingRef.current = false
                }
              }, 150)
            }
          }}
          initialSlide={Math.max(0, Math.min(targetIndexRef.current, scans.length - 1))}
          onSlideChange={handleSlideChange}
          slidesPerView={1}
          centeredSlides={true}
          autoHeight={true}
          loop={isLoopMode}
          spaceBetween={'50px'}
          style={{
            width: '100vw',
            height: '100dvh',
            maxHeight: '100dvh',
          }}
        >
          <BlockchainVerifiedModalHeader
            slot="container-start"
            currentIndex={currentIndex}
            handleNext={handleNext}
            handlePrev={handlePrev}
            scanLength={scans.length}
            primaryColor={primaryColor}
            walletAddress={walletAddress}
            showWalletAddress={showWalletAddress}
            accountImage={accountImage}
          />

          {scans.map((scan) => (
            <SwiperSlide
              key={scan.id}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                id="index__components-blockchain-verified-banner-blockchain-modal__box__l141"
                sx={{
                  maxWidth: '800px',
                  width: '100%',
                  margin: '0 auto',
                  height: '100%',
                  maxHeight: `calc(100dvh - ${headerHeight()}px)`,
                  overflowY: 'auto',
                  padding: '20px 32px',
                }}
              >
                <Typography
                  id="index__components-blockchain-verified-banner-blockchain-modal__typography__l152"
                  sx={{ pb: '20px', fontSize: '16px', fontWeight: 700, textAlign: 'center' }}
                >
                  {formatDate(scan.created_at)}
                </Typography>

                {scan?.scanner_info && scan.scanner_info && (
                  <Divider
                    id="index__components-blockchain-verified-banner-blockchain-modal__divider__l159"
                    sx={{ my: '10px', borderStyle: 'dashed', borderWidth: '1px' }}
                  />
                )}

                {scan?.scanner_info && scan.scanner_info && (
                  <Typography
                    id="index__components-blockchain-verified-banner-blockchain-modal__typography__l163"
                    sx={{ pb: '10px', fontSize: '16px', fontWeight: 600 }}
                  >
                    Scanner details:
                  </Typography>
                )}

                {scan?.scanner_info && scan.scanner_info?.name && (
                  <Box
                    id="index__components-blockchain-verified-banner-blockchain-modal__box__l169"
                    sx={{ display: 'flex', alignItems: 'center', gap: '15px', pb: '5px' }}
                  >
                    {scan?.scanner_image_small && (
                      <Box
                        id="index__components-blockchain-verified-banner-blockchain-modal__box__l171"
                        sx={{
                          backgroundImage: `url(${scan.scanner_image_small})`,
                          backgroundPosition: 'center',
                          backgroundSize: 'cover',
                          backgroundRepeat: 'no-repeat',
                          borderRadius: '50%',
                          width: '45px',
                          height: '45px',
                        }}
                      />
                    )}
                    <BlockchainModalItem
                      value={scan.scanner_info.name}
                      containerSx={{ pb: '0px' }}
                    />
                  </Box>
                )}

                {scan?.scanner_info &&
                  (scan.scanner_info.latitude !== 0 || scan.scanner_info.longitude !== 0) && (
                    <BlockchainModalItem
                      label={'Location'}
                      value={
                        <BlockchainMapItem
                          // locations={scan.location_info.locations}
                          scannerInfo={scan.scanner_info}
                          primaryColor={primaryColor}
                        />
                      }
                      containerSx={{ pb: '5px' }}
                    />
                  )}

                {scan?.scanner_info && scan.scanner_info?.email && (
                  <BlockchainModalItem label={'Email'} value={scan.scanner_info.email} />
                )}

                {scan?.scanner_info &&
                  (scan.scanner_info.latitude !== 0 || scan.scanner_info.longitude !== 0) &&
                  scan.location_info &&
                  scan.location_info.locations &&
                  checkGeofenceStatus(scan.location_info.locations) && (
                    <BlockchainModalItem
                      label={t('blockchain-verified-modal-geofence-lable')}
                      value={'Within Geofence'}
                    />
                  )}

                {scan?.scanner_info && scan.scanner_info?.wallet && (
                  <Box
                    id="index__components-blockchain-verified-banner-blockchain-modal__box__l221"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      id="index__components-blockchain-verified-banner-blockchain-modal__box__l229"
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '5px',
                      }}
                    >
                      <Typography
                        id="index__components-blockchain-verified-banner-blockchain-modal__typography__l236"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: COLORS.GREY_LIGHT,
                        }}
                      >
                        {'Scanner address'}:
                      </Typography>

                      <Typography
                        id="index__components-blockchain-verified-banner-blockchain-modal__typography__l246"
                        sx={{
                          fontSize: '10px',
                          color: COLORS.GREY_LIGHT,
                        }}
                      >
                        {isMobile
                          ? truncateAddress(scan.scanner_info.wallet)
                          : scan.scanner_info.wallet}
                      </Typography>
                    </Box>
                    <IconButton
                      id="index__components-blockchain-verified-banner-blockchain-modal__iconbutton__l257"
                      component="a"
                      href={getPolygonscanAddressUrl(scan.scanner_info.wallet)}
                      target="_blank"
                      sx={{
                        p: '0px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ExternalLinkIcon size={16} color={COLORS.GREY_LIGHT} />
                    </IconButton>
                  </Box>
                )}

                {scan?.scanner_info && scan.scanner_info && (
                  <Divider
                    id="index__components-blockchain-verified-banner-blockchain-modal__divider__l274"
                    sx={{ my: '10px', borderStyle: 'dashed', borderWidth: '1px' }}
                  />
                )}

                {scan?.scanner_info && scan.scanner_info && (
                  <Typography
                    id="index__components-blockchain-verified-banner-blockchain-modal__typography__l278"
                    sx={{ pb: '10px', fontSize: '16px', fontWeight: 600 }}
                  >
                    Scan details:
                  </Typography>
                )}

                {scanName && (
                  <Box
                    id="index__components-blockchain-verified-banner-blockchain-modal__box__l284"
                    sx={{ pb: '10px', display: 'flex', alignItems: 'center', gap: '15px' }}
                  >
                    {scanImage && (
                      <AvatarImg
                        id="index__components-blockchain-verified-banner-blockchain-modal__avatarimg__l286"
                        borderRadius={'6px'}
                        width={'65px'}
                        src={scanImage}
                        alt="Scan Image"
                      />
                    )}
                    <Typography
                      id="index__components-blockchain-verified-banner-blockchain-modal__typography__l293"
                      sx={{ fontSize: '16px' }}
                    >
                      {scanName}
                    </Typography>
                  </Box>
                )}

                {impactName && (
                  <Box
                    id="index__components-blockchain-verified-banner-blockchain-modal__box__l298"
                    sx={{ pb: '10px', display: 'flex', alignItems: 'center', gap: '15px' }}
                  >
                    {impactImage && (
                      <AvatarImg
                        id="index__components-blockchain-verified-banner-blockchain-modal__avatarimg__l300"
                        borderRadius={'6px'}
                        width={'65px'}
                        src={impactImage}
                        alt="Scan Image"
                      />
                    )}
                    <Typography
                      id="index__components-blockchain-verified-banner-blockchain-modal__typography__l307"
                      sx={{ fontSize: '16px' }}
                    >
                      {impactName}
                    </Typography>
                  </Box>
                )}

                {scan.batch_id && (
                  <BlockchainModalItem
                    label={t('blockchain-verified-modal-batch-id-lable')}
                    value={scan.batch_id}
                  />
                )}
                {scan?.note && (
                  <BlockchainModalItem
                    label={t('blockchain-verified-modal-note-lable')}
                    value={scan.note}
                    column
                  />
                )}
                {scan?.media_info?.media && scan.media_info.media.length > 0 && (
                  <MediaSection media={scan.media_info.media} />
                )}

                {scan?.account_tx && (
                  <Box
                    id="index__components-blockchain-verified-banner-blockchain-modal__box__l329"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      pb: '20px',
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      id="index__components-blockchain-verified-banner-blockchain-modal__box__l338"
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '6px',
                        maxWidth: 'calc(100% - 35px)',
                        justifyContent: 'start',
                      }}
                    >
                      <Typography
                        id="index__components-blockchain-verified-banner-blockchain-modal__typography__l347"
                        sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.GREY_LIGHT }}
                      >
                        {t('blockchain-verified-modal-tx-lable')}:
                      </Typography>
                      <Typography
                        id="index__components-blockchain-verified-banner-blockchain-modal__typography__l352"
                        sx={{
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: COLORS.GREY_LIGHT,
                        }}
                      >
                        {isMobile ? (
                          <Box
                            id="index__components-blockchain-verified-banner-blockchain-modal__box__l362"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Typography
                              id="index__components-blockchain-verified-banner-blockchain-modal__typography__l363"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {scan.account_tx.slice(0, -4)}
                            </Typography>

                            <Typography
                              id="index__components-blockchain-verified-banner-blockchain-modal__typography__l373"
                              sx={{ ml: '-3px' }}
                            >
                              {scan.account_tx.slice(-4)}
                            </Typography>
                          </Box>
                        ) : (
                          scan.account_tx
                        )}
                      </Typography>
                    </Box>
                    <IconButton
                      id="index__components-blockchain-verified-banner-blockchain-modal__iconbutton__l380"
                      component="a"
                      href={getScanExplorerTxUrl(scan.account_tx, scan.network)}
                      target="_blank"
                      sx={{ p: '3px', width: '26px' }}
                    >
                      <ExternalLinkIcon size={20} color={COLORS.GREY_LIGHT} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
        <IconButton
          id="index__components-blockchain-verified-banner-blockchain-modal__iconbutton__l394"
          onClick={handleClose}
          sx={{
            position: 'fixed',
            top: 4,
            right: 8,
            zIndex: 100,
            color: theme.palette.common.black,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </CustomModalAnimated>
  )
}
